/**
 * The platform tools: every staff surface outside a tenant, the sidebar in platform scope. One
 * entry per tool, in one place (reboxed's `APP_REGISTRY` is the model), so a ported tool declares
 * its route, its group and what it replaces once, and `docs/admin-tools.md` can be checked
 * against this list. Tenant-scoped sections take a `$tenantId` and stay in `TENANT_NAV`
 * (`lib/tenantNav.ts`). The ⌘K palette indexes both lists, so a tool registered here is
 * searchable with no palette entry of its own.
 */
import type { LinkProps } from "@tanstack/react-router";
import {
  Boxes,
  Building2,
  ClipboardList,
  ListChecks,
  type LucideIcon,
  OctagonX,
  Package,
  PackageCheck,
  Palette,
  Radar,
  Scale,
  ScrollText,
  Search,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Swords,
  Tags,
  Truck,
  Volume2,
  Wallet,
  Warehouse,
} from "lucide-react";

/** A registered top-level path: the router's own union, minus the tenant tree and sign-in. */
export type PlatformToolPath = Exclude<
  Extract<LinkProps["to"], `/${string}`>,
  | `/tenants${string}`
  | `/login${string}`
  | `/accept-invite${string}`
  | `/accept-admin-invite${string}`
  | `/orgs${string}`
>;

/** Where a tool came from: a boxed Retool app, or a reboxed app. */
export interface ToolSource {
  kind: "retool" | "reboxed";
  app: string;
}

/**
 * Sidebar sections in display order, each a labelled list of tools (the first has no label,
 * like the top of every sidebar). The names say what the tools do, and each leaves room for the
 * ledger's unshipped surfaces: kill switches beside the audit log, images and catalog items
 * beside the box templates, packing and procurement beside the orders.
 */
export const PLATFORM_TOOL_GROUPS = [
  { key: "platform", label: null },
  { key: "storefront-defaults", label: "Storefront defaults" },
  { key: "catalog", label: "Catalog" },
  { key: "fulfillment", label: "Fulfillment" },
] as const;
export type PlatformToolGroupKey = (typeof PLATFORM_TOOL_GROUPS)[number]["key"];

export interface PlatformTool {
  /** Stable id, also the row key in `docs/admin-tools.md`. */
  key: string;
  label: string;
  /** One sentence: what a staff member does here. */
  description: string;
  to: PlatformToolPath;
  icon: LucideIcon;
  group: PlatformToolGroupKey;
  /** Highlight only on the exact path, for an index page whose children have their own entries. */
  exact: boolean;
  /** What this tool replaces; empty for something only Loot OS needed. */
  replaces: readonly ToolSource[];
  /** Terms staff call the tool by that its label does not say; the ⌘K palette matches them. */
  keywords?: readonly string[];
}

/** Registry order is display order within a section, so the list reads section by section. */
export const PLATFORM_TOOLS: readonly PlatformTool[] = [
  {
    key: "tenants",
    label: "Tenants",
    description: "Every tenant on the platform: create, archive, restore, open one.",
    to: "/",
    icon: Building2,
    group: "platform",
    exact: true,
    replaces: [],
    keywords: ["home", "orgs", "customers"],
  },
  {
    key: "platform-admins",
    label: "Platform admins",
    description: "Who runs the platform: grant and revoke platform access.",
    to: "/platform-admins",
    icon: ShieldCheck,
    group: "platform",
    exact: false,
    replaces: [
      { kind: "retool", app: "Cognito Group Management" },
      { kind: "reboxed", app: "Permissions Manager" },
    ],
    keywords: ["staff", "invites"],
  },
  {
    key: "audit-log",
    label: "Audit log",
    description: "Every action taken with authority, by actor, tenant or target.",
    to: "/audit",
    icon: ScrollText,
    group: "platform",
    exact: false,
    replaces: [{ kind: "retool", app: "Search Audit Logs" }],
    keywords: ["history", "logs"],
  },
  {
    key: "platform-switches",
    label: "Platform Switches",
    description:
      "Enable or disable opens, claims, top-ups, withdrawals, battles and buying, platform-wide.",
    to: "/platform-switches",
    icon: OctagonX,
    group: "platform",
    exact: false,
    replaces: [{ kind: "retool", app: "Kill Switches" }],
  },
  {
    key: "shopify-install",
    label: "Shopify install link",
    description:
      "The link that installs this stage's Shopify app on one store, which sets the store up as its own tenant.",
    to: "/shopify-install",
    icon: ShoppingBag,
    group: "platform",
    exact: false,
    replaces: [],
    keywords: ["merchant", "onboarding", "dev store"],
  },
  {
    key: "shipping",
    label: "Shipping",
    description:
      "The default destinations, costs and delivery estimates every environment inherits.",
    to: "/shipping",
    icon: Truck,
    group: "storefront-defaults",
    exact: false,
    replaces: [{ kind: "reboxed", app: "Shipping Config" }],
  },
  {
    key: "battles",
    label: "Battles",
    description:
      "The loser prize every battle hands its non-winning players: the image every environment inherits.",
    to: "/battles",
    icon: Swords,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
  },
  {
    key: "sounds",
    label: "Sounds",
    description:
      "The sound every storefront's openers play for each event until a tenant sets its own.",
    to: "/sounds",
    icon: Volume2,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
  },
  {
    key: "opener-styles",
    label: "Opener styles",
    description:
      "How boxes open: the animations and color overrides every tenant can use, and the default every environment inherits.",
    to: "/opener-styles",
    icon: Sparkles,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
    keywords: ["spinner", "animation", "opener", "reel", "opening animation"],
  },
  {
    key: "breaks-studio",
    label: "Breaks Studio",
    description:
      "Design the cube opener: its look, light, energy and sound, its panels, and the demo.",
    to: "/breaks-studio",
    icon: Palette,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
    keywords: ["cube opener", "box editor", "panel designer", "demo", "opener"],
  },
  {
    key: "deposits",
    label: "Deposits",
    description:
      "The presets every storefront's Deposit offers and the least and most a shopper may deposit, until its tenant sets its own.",
    to: "/deposits",
    icon: Wallet,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
    keywords: ["deposit", "top up", "presets", "minimum", "maximum", "payments", "stripe"],
  },
  {
    // One page for the three defaults the tenant's Storefront → Legal / Consent overrides, in
    // its order, so a default and the override that inherits it sit in the same place on
    // either side of the scope switch.
    key: "legal",
    label: "Legal / Consent",
    description:
      "The company every storefront's Terms and Privacy Policy name, and the Termly website and banner style its cookie banner runs on, until its tenant sets its own.",
    to: "/legal",
    icon: Scale,
    group: "storefront-defaults",
    exact: false,
    replaces: [],
    keywords: ["terms", "privacy", "policy", "termly", "consent", "cookies", "gdpr", "banner"],
  },
  {
    key: "box-templates",
    label: "Box templates",
    description: "Boxes the platform publishes for every tenant to offer.",
    // `Boxes`, not `Package`: that one is the tenant's own Boxes tab.
    to: "/templates",
    icon: Boxes,
    group: "catalog",
    exact: false,
    replaces: [],
  },
  {
    key: "catalog-items",
    label: "Catalog items",
    description:
      "Create a catalog item, or import a CSV of TCGplayer cards (name, product id, game, printing).",
    to: "/fulfillment/items",
    icon: Package,
    group: "catalog",
    exact: false,
    replaces: [
      { kind: "retool", app: "Asset Group Creator" },
      { kind: "retool", app: "Asset Group Editor" },
      { kind: "reboxed", app: "Asset Groups" },
    ],
    keywords: ["asset group", "create item", "inventory", "bulk import", "csv"],
  },
  {
    key: "inventory",
    label: "Inventory",
    description:
      "Count on-hand stock by kind and category. Set a quantity, or download the filtered list.",
    to: "/fulfillment/inventory",
    icon: Warehouse,
    group: "catalog",
    exact: true,
    replaces: [],
    keywords: ["count", "quantity", "stock", "slab", "sealed"],
  },
  {
    key: "listings",
    label: "Listings",
    description: "Vendor listings across the catalog, filtered by title, vendor and status.",
    to: "/fulfillment/listings",
    icon: Tags,
    group: "fulfillment",
    exact: false,
    replaces: [],
  },
  {
    key: "orders",
    label: "Orders",
    description: "Fulfillment orders across every tenant, and the buy facts for a manual buy.",
    to: "/fulfillment/orders",
    icon: ClipboardList,
    group: "fulfillment",
    exact: false,
    replaces: [],
    keywords: ["purchase", "manual buy", "withdrawals"],
  },
  {
    key: "procurement",
    label: "Procurement",
    description: "Buying queue, packing lists, and per-card dispositions.",
    to: "/fulfillment/procurement",
    icon: ShoppingCart,
    group: "fulfillment",
    exact: false,
    replaces: [{ kind: "retool", app: "Procurement Requests" }],
    keywords: ["buying", "tcgplayer", "purchase order"],
  },
  {
    key: "packing",
    label: "Packing",
    description: "Bags waiting to be picked: take a range, then work one combined card list.",
    to: "/fulfillment/packing",
    icon: PackageCheck,
    group: "fulfillment",
    exact: false,
    replaces: [{ kind: "retool", app: "TCG Pack Orders" }],
    keywords: ["pick", "pack", "warehouse", "queue", "orders"],
  },
  {
    key: "stockx",
    label: "StockX",
    description: "Search StockX and fetch a product's variants and market data into the catalog.",
    to: "/fulfillment/stockx",
    icon: Search,
    group: "fulfillment",
    exact: true,
    replaces: [{ kind: "reboxed", app: "StockX API Explorer" }],
  },
  {
    key: "stockx-fetch-queue",
    label: "Fetch queue",
    description: "Product fetches in flight and their outcomes.",
    to: "/fulfillment/stockx/queue",
    icon: ListChecks,
    group: "fulfillment",
    exact: false,
    replaces: [],
  },
  {
    key: "vendor-product-sourcing",
    label: "Vendor product sourcing",
    description:
      "The sourcing rules: the StockX pages the daily sourcing cron lists new products from, and for which tenants.",
    to: "/fulfillment/stockx/sourcing",
    icon: Radar,
    group: "fulfillment",
    exact: false,
    replaces: [],
    keywords: ["sourcing", "sourcing rules", "new products", "auto promote", "sneakers"],
  },
];

/** One section of the sidebar in platform scope: a label (none for the top) and its tools. */
export interface PlatformNavGroup {
  key: PlatformToolGroupKey;
  label: string | null;
  tools: readonly PlatformTool[];
}

/**
 * The tools as the sidebar renders them: a section per group in `PLATFORM_TOOL_GROUPS` order,
 * an empty one dropped.
 */
export function platformNavGroups(
  tools: readonly PlatformTool[] = PLATFORM_TOOLS,
): PlatformNavGroup[] {
  return PLATFORM_TOOL_GROUPS.flatMap((group): PlatformNavGroup[] => {
    const items = tools.filter((tool) => tool.group === group.key);
    return items.length === 0 ? [] : [{ key: group.key, label: group.label, tools: items }];
  });
}
